import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Truck, Upload, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fileToB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

const uploadFile = async (b64) => {
  const r = await axios.post(`${API}/trailer-hire/upload-file`, { file_data: b64, file_type: 'photo' });
  return r.data.file_id;
};

const FEATURES = [
  ['road_train_rated', 'Road Train Rated'],
  ['mez_deck', 'Mez Deck'],
  ['deck_widener', 'Deck Widener'],
  ['extendable', 'Extendable'],
  ['walking', 'Walking Floor'],
  ['dg_rated', 'DG Rated'],
  ['tail_gate_lift', 'Tail Gate Lift'],
  ['ramps', 'Ramps'],
  ['beaver_tail', 'Beaver Tail']
];

const TRUCK_TYPES = [
  'Rigid Tipper',
  'Rigid Tautliner',
  'Rigid Pantech',
  'Rigid Flat Top',
  'Rigid Tanker',
  'Rigid Agi',
  'Tilt Tray',
  'Truck & Dog',
  'Prime Mover',
  'Other'
];

const AXLE_SPACING_OPTIONS = [
  { v: 'standard_semi',    label: 'Standard Semi' },
  { v: 'all_1_8',          label: 'All at 1.8m' },
  { v: '4at1_2_2_4_1_2',   label: '4 @ 1.2 / 2.4 / 1.2' },
  { v: 'other',            label: 'Other' }
];

const ListTrailer = () => {
  const navigate = useNavigate();
  const userEmail = localStorage.getItem('user_email');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [previews, setPreviews] = useState({});  // index -> dataURL
  const [photoIds, setPhotoIds] = useState({});  // index -> file_id (3 photos; index 2 is rear)
  const [form, setForm] = useState({
    operator_email: userEmail || '',
    operator_phone: '',
    operator_company_name: '',
    vehicle_category: 'trailer',          // 'trailer' or 'truck'
    // truck-only
    truck_type: '',
    truck_type_other: '',
    pallet_spaces: '',
    road_train_rated_truck: false,
    turntable_size: '',
    has_tailgate: false,
    tailgate_weight_limit: '',
    has_ramps: false,
    // shared
    trailer_type: '',
    number_of_axles: '',
    tyres_per_axle: '',
    gvm: '',
    gcm: '',
    average_payload: '',
    deck_length: '',
    coupling_type: '',
    base_location: '',
    axle_spacing: 'standard_semi',
    axle_spacing_other: '',
    wa_period_permit: false,
    wa_period_permit_details: '',
    indicative_daily_rate: '',
    indicative_weekly_rate: '',
    indicative_monthly_rate: '',
    notes: '',
    ...Object.fromEntries(FEATURES.map(([k]) => [k, false]))
  });

  if (!userEmail) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <p>Please log in as a carrier to list a trailer.</p>
          <Button onClick={() => navigate('/login')} className="mt-4">Log in</Button>
        </div>
      </div>
    );
  }

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const toggle = (k) => () => setForm(p => ({ ...p, [k]: !p[k] }));

  const handlePhoto = async (idx, file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error('Max 6MB per photo'); return; }
    const b64 = await fileToB64(file);
    setPreviews(p => ({ ...p, [idx]: b64 }));
    try {
      const fileId = await uploadFile(b64);
      setPhotoIds(p => ({ ...p, [idx]: fileId }));
      toast.success(`Photo ${idx + 1} uploaded`);
    } catch {
      toast.error('Upload failed');
    }
  };

  const submit = async () => {
    const isTruck = form.vehicle_category === 'truck';
    const sharedRequired = ['number_of_axles', 'tyres_per_axle', 'gvm', 'gcm', 'average_payload', 'base_location'];
    let required;
    if (isTruck) {
      required = [...sharedRequired, 'truck_type', 'pallet_spaces'];
    } else {
      required = [...sharedRequired, 'trailer_type', 'deck_length', 'coupling_type'];
    }
    const missing = required.filter(f => !form[f]);
    if (missing.length) { toast.error(`Please fill: ${missing.join(', ')}`); return; }
    if (isTruck && form.truck_type === 'Other' && !form.truck_type_other) {
      toast.error('Please specify the truck type');
      return;
    }
    const ids = [photoIds[0], photoIds[1], photoIds[2]].filter(Boolean);
    if (ids.length < 3) { toast.error('Please upload 3 photos (the 3rd must be a rear 3/4 with rego visible)'); return; }
    if (form.wa_period_permit && !form.wa_period_permit_details) {
      toast.error('Specify which type of WA Period Permit');
      return;
    }
    if (form.axle_spacing === 'other' && !form.axle_spacing_other) {
      toast.error('Specify the axle spacing');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        // For trucks, set trailer_type to the truck label so legacy code paths still see a value
        trailer_type: isTruck
          ? (form.truck_type === 'Other' ? form.truck_type_other : form.truck_type)
          : form.trailer_type,
        photo_ids: ids,
        rear_photo_id: photoIds[2]
      };
      await axios.post(`${API}/trailer-hire/listings`, payload);
      setSubmitted(true);
      toast.success('Listing published');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create listing');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center max-w-xl">
          <Card className="p-8">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#1e3a5f] mb-2">Listing live</h1>
            <p className="text-gray-600 mb-5">Your trailer is now visible on the public hire board.</p>
            <Button onClick={() => navigate('/trailer-hire')} className="bg-[#1e3a5f] hover:bg-[#15304f] mr-2">View board</Button>
            <Button onClick={() => navigate('/carrier-dashboard')} variant="outline">Back to dashboard</Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-[#1e3a5f] text-white py-8">
        <div className="container mx-auto px-4 max-w-3xl flex items-center gap-3">
          <Truck className="w-8 h-8 text-red-300" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">List Your Truck or Trailer</h1>
            <p className="text-blue-100 text-sm">Free for active paid subscribers. Adhoc operators are charged $49 only when a hire is confirmed (no hire = no fee).</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-3xl space-y-5">
        {/* Vehicle category */}
        <Card className="p-5">
          <h2 className="font-bold text-[#1e3a5f] mb-3">What are you listing?</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[{v:'trailer',label:'Trailer'},{v:'truck',label:'Truck'}].map(opt => (
              <label key={opt.v} className={`flex items-center gap-2 p-3 rounded border cursor-pointer ${form.vehicle_category === opt.v ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
                <input type="radio" name="vehicle_category" value={opt.v} checked={form.vehicle_category === opt.v} onChange={set('vehicle_category')} data-testid={`category-${opt.v}`} />
                <span className="font-medium">{opt.label}</span>
              </label>
            ))}
          </div>
        </Card>

        {/* Truck-only fields */}
        {form.vehicle_category === 'truck' && (
          <Card className="p-5">
            <h2 className="font-bold text-[#1e3a5f] mb-3">Truck details</h2>
            <div className="space-y-3">
              <div>
                <Label>Truck Type *</Label>
                <select
                  value={form.truck_type}
                  onChange={set('truck_type')}
                  className="w-full h-10 px-3 mt-1 text-sm border border-gray-300 rounded-md"
                  data-testid="truck-type"
                >
                  <option value="">Select…</option>
                  {TRUCK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {form.truck_type === 'Other' && (
                <div>
                  <Label>Specify truck type *</Label>
                  <Input value={form.truck_type_other} onChange={set('truck_type_other')} data-testid="truck-type-other" />
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>GVM *</Label><Input value={form.gvm} onChange={set('gvm')} placeholder="e.g. 16,000 kg" data-testid="truck-gvm" /></div>
                <div><Label>GCM *</Label><Input value={form.gcm} onChange={set('gcm')} placeholder="e.g. 42,500 kg" data-testid="truck-gcm" /></div>
                <div><Label>Number of Axles *</Label><Input value={form.number_of_axles} onChange={set('number_of_axles')} placeholder="e.g. 3" data-testid="truck-axles" /></div>
                <div><Label>Tyres per Axle *</Label><Input value={form.tyres_per_axle} onChange={set('tyres_per_axle')} placeholder="e.g. 4" data-testid="truck-tyres" /></div>
                <div><Label>Average Payload *</Label><Input value={form.average_payload} onChange={set('average_payload')} placeholder="e.g. 10T" data-testid="truck-payload" /></div>
                <div><Label>Pallet Spaces *</Label><Input value={form.pallet_spaces} onChange={set('pallet_spaces')} placeholder="e.g. 14" data-testid="truck-pallets" /></div>
              </div>

              {/* Prime Mover conditional */}
              {form.truck_type === 'Prime Mover' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.road_train_rated_truck} onChange={toggle('road_train_rated_truck')} data-testid="pm-road-train" />
                    <span className="text-sm">Road Train Rated?</span>
                  </label>
                  <div className="mt-2">
                    <Label>Turntable Size *</Label>
                    <Input value={form.turntable_size} onChange={set('turntable_size')} placeholder="e.g. 50mm / 90mm" data-testid="pm-turntable" />
                  </div>
                </div>
              )}

              {/* Rigid Tautliner / Pantech tailgate */}
              {(form.truck_type === 'Rigid Tautliner' || form.truck_type === 'Rigid Pantech') && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.has_tailgate} onChange={toggle('has_tailgate')} data-testid="tail-has" />
                    <span className="text-sm">Tailgate fitted?</span>
                  </label>
                  {form.has_tailgate && (
                    <div className="mt-2">
                      <Label>Tailgate weight limit</Label>
                      <Input value={form.tailgate_weight_limit} onChange={set('tailgate_weight_limit')} placeholder="e.g. 1,000 kg" data-testid="tail-weight" />
                    </div>
                  )}
                </div>
              )}

              {/* Rigid Tipper / Flat Top ramps */}
              {(form.truck_type === 'Rigid Tipper' || form.truck_type === 'Rigid Flat Top') && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.has_ramps} onChange={toggle('has_ramps')} data-testid="truck-has-ramps" />
                    <span className="text-sm">Has ramps?</span>
                  </label>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Trailer-only specs */}
        {form.vehicle_category === 'trailer' && (
          <Card className="p-5">
            <h2 className="font-bold text-[#1e3a5f] mb-3">Trailer specs</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Trailer Type *</Label><Input value={form.trailer_type} onChange={set('trailer_type')} placeholder="e.g. Drop deck" data-testid="trailer-type" /></div>
              <div><Label>Number of Axles *</Label><Input value={form.number_of_axles} onChange={set('number_of_axles')} placeholder="e.g. 3" data-testid="axles" /></div>
              <div><Label>Tyres per Axle *</Label><Input value={form.tyres_per_axle} onChange={set('tyres_per_axle')} placeholder="e.g. 4 (super-single 2)" data-testid="tyres" /></div>
              <div><Label>GVM *</Label><Input value={form.gvm} onChange={set('gvm')} placeholder="e.g. 35,000 kg" data-testid="gvm" /></div>
              <div><Label>GCM *</Label><Input value={form.gcm} onChange={set('gcm')} placeholder="e.g. 100,000 kg" data-testid="gcm" /></div>
              <div><Label>Average Payload *</Label><Input value={form.average_payload} onChange={set('average_payload')} placeholder="e.g. 25T" data-testid="payload" /></div>
              <div><Label>Deck Length *</Label><Input value={form.deck_length} onChange={set('deck_length')} placeholder="e.g. 13.5 m" data-testid="deck-length" /></div>
              <div><Label>Coupling Type *</Label><Input value={form.coupling_type} onChange={set('coupling_type')} placeholder="e.g. 50mm / JOST / Ringfeder" data-testid="coupling" /></div>
            </div>
          </Card>
        )}

        {/* Base location — shared */}
        <Card className="p-5">
          <Label>Base Location (Suburb + State) *</Label>
          <Input value={form.base_location} onChange={set('base_location')} placeholder="e.g. Adelaide SA" className="mt-1" data-testid="base-location" />
        </Card>

        {/* Axle spacing — trailer only */}
        {form.vehicle_category === 'trailer' && (
          <Card className="p-5">
            <h2 className="font-bold text-[#1e3a5f] mb-3">Axle spacing</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {AXLE_SPACING_OPTIONS.map(o => (
              <label key={o.v} className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${form.axle_spacing === o.v ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
                <input type="radio" name="axle_spacing" value={o.v} checked={form.axle_spacing === o.v} onChange={set('axle_spacing')} data-testid={`axle-${o.v}`} />
                <span className="text-sm">{o.label}</span>
              </label>
            ))}
          </div>
          {form.axle_spacing === 'other' && (
            <div className="mt-3">
              <Label>Specify spacing</Label>
              <Input value={form.axle_spacing_other} onChange={set('axle_spacing_other')} placeholder="Describe axle spacing" data-testid="axle-other-text" />
            </div>
          )}
        </Card>
        )}

        {/* Features — trailer only */}
        {form.vehicle_category === 'trailer' && (
        <Card className="p-5">
          <h2 className="font-bold text-[#1e3a5f] mb-3">Capabilities</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {FEATURES.map(([k, label]) => (
              <label key={k} className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${form[k] ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
                <input type="checkbox" checked={form[k]} onChange={toggle(k)} data-testid={`cap-${k}`} />
                <span className="text-sm">{label}</span>
              </label>
            ))}
            <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${form.wa_period_permit ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
              <input type="checkbox" checked={form.wa_period_permit} onChange={toggle('wa_period_permit')} data-testid="cap-wa_period_permit" />
              <span className="text-sm">WA Period Permit</span>
            </label>
          </div>
          {form.wa_period_permit && (
            <div className="mt-3">
              <Label>Which permit type? *</Label>
              <Input value={form.wa_period_permit_details} onChange={set('wa_period_permit_details')} placeholder="e.g. NHVR Class 1, OSOM..." data-testid="wa-permit-details" />
            </div>
          )}
        </Card>
        )}

        {/* Photos */}
        <Card className="p-5">
          <h2 className="font-bold text-[#1e3a5f] mb-1">Photos</h2>
          <p className="text-xs text-gray-600 mb-3">Upload 3 photos. The 3rd <strong>must be a rear 3/4 shot with the registration clearly visible</strong>.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[0, 1, 2].map(idx => (
              <div key={idx} className="border-2 border-dashed border-gray-300 rounded p-3 text-center">
                <p className="text-xs text-gray-500 mb-2">
                  {idx === 2 ? 'Rear 3/4 (rego visible)' : `Photo ${idx + 1}`}
                </p>
                {previews[idx] ? (
                  <img src={previews[idx]} alt="" className="w-full h-32 object-cover rounded mb-2" />
                ) : (
                  <div className="h-32 flex items-center justify-center bg-gray-50 rounded mb-2">
                    <Upload className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <Input type="file" accept="image/*" onChange={(e) => handlePhoto(idx, e.target.files[0])} data-testid={`photo-${idx}`} />
                {photoIds[idx] && <p className="text-xs text-green-600 mt-1">✓ uploaded</p>}
              </div>
            ))}
          </div>
        </Card>

        {/* Optional rates / notes */}
        <Card className="p-5">
          <h2 className="font-bold text-[#1e3a5f] mb-3">Indicative pricing (optional)</h2>
          <p className="text-xs text-gray-600 mb-3">
            Helps hirers gauge cost. You&apos;ll confirm the actual rate per booking.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>$ / day</Label><Input value={form.indicative_daily_rate} onChange={set('indicative_daily_rate')} data-testid="rate-day" /></div>
            <div><Label>$ / week</Label><Input value={form.indicative_weekly_rate} onChange={set('indicative_weekly_rate')} data-testid="rate-week" /></div>
            <div><Label>$ / month</Label><Input value={form.indicative_monthly_rate} onChange={set('indicative_monthly_rate')} data-testid="rate-month" /></div>
          </div>
          <div className="mt-3">
            <Label>Notes (optional)</Label>
            <Input value={form.notes} onChange={set('notes')} placeholder="Anything else hirers should know" data-testid="notes" />
          </div>
        </Card>

        <Button
          onClick={submit}
          disabled={submitting}
          className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-base font-bold"
          data-testid="submit-listing"
        >
          {submitting ? 'Publishing…' : 'Publish listing'}
        </Button>
      </div>

      <Footer />
    </div>
  );
};

export default ListTrailer;
